/**
 * api/_rfpFacts.js — the FACTS step of a consultant RFP: the questions, which consultant gets
 * which, and how each answer prints. PURE (no I/O) — the send window imports it across the api/
 * boundary to ask, the RFP PDF imports it to print, so the two can never disagree.
 *
 * Answers are stored per PROJECT in Supabase `project_engineering_facts` (database/360) as
 * { <key>: <option value or text> }, so a fact answered for the structural RFP is already there
 * for the civil one. 'unknown' is a real answer: it prints as "To be confirmed", so the engineer
 * sees what is open instead of assuming it (an engineer's own intake form says blanks are assumed
 * at code minimums).
 *
 * The structural questions come from the intake form K&K Structural sends its architects
 * (Kamran Askarian, Oct 2026). Add a service's questions by adding entries with that service in
 * `for` — nothing else changes.
 */

const YES_NO = [{ v: 'yes', l: 'Yes' }, { v: 'no', l: 'No' }, { v: 'unknown', l: "Don't know" }]
const STRUCT = ['Structural Engineer']
// Whoever prepares the Title 24 calcs: the energy consultant, or an MEP engineer whose services
// include them (Matt, 2026-10-28: an MEP engineer's RFP must ask for the calcs too).
export const ENERGY = ['Energy Consultant', 'Mechanical Engineer', 'Mechanical and Electrical Engineer']
const isNew = (f) => f.work_type === 'new'

export const RFP_QUESTIONS = [
  // ── every consultant ──
  { key: 'work_type', label: 'Type of work', for: 'all', options: [
    { v: 'new', l: 'New construction' }, { v: 'addition', l: 'Addition' },
    { v: 'remodel', l: 'Remodel' }, { v: 'addition_remodel', l: 'Addition and remodel' },
  ] },
  { key: 'soils_report', label: 'Soils (geotechnical) report', for: 'all', options: [
    { v: 'existing', l: 'Existing report available' }, { v: 'ordered', l: 'New report ordered' },
    { v: 'needed', l: 'New report needed, not ordered' }, { v: 'not_required', l: 'Not required' },
    { v: 'unknown', l: "Don't know" },
  ] },
  { key: 'survey', label: 'Topographic survey', for: 'all', options: [
    { v: 'available', l: 'Available' }, { v: 'ordered', l: 'Ordered' },
    { v: 'none', l: 'None' }, { v: 'unknown', l: "Don't know" },
  ] },
  { key: 'asbuilt_arch', label: 'Architectural as-built drawings available', for: 'all', options: YES_NO, when: (f) => !isNew(f) },
  { key: 'asbuilt_struct', label: 'Structural or recorded drawings available', for: 'all', options: YES_NO, when: (f) => !isNew(f) },

  // ── structural (K&K's intake form) ──
  { key: 'existing_foundation', label: 'Existing foundation', for: STRUCT, when: (f) => !isNew(f), options: [
    { v: 'footing', l: 'Conventional footing' }, { v: 'raised', l: 'Raised floor' },
    { v: 'slab', l: 'Slab on grade' }, { v: 'unknown', l: "Don't know" },
  ] },
  { key: 'new_foundation', label: 'New foundation', for: STRUCT, options: [
    { v: 'footing', l: 'Conventional footing' }, { v: 'raised', l: 'Raised floor' },
    { v: 'none', l: 'No new foundation' }, { v: 'engineer', l: 'Engineer to recommend' },
  ] },
  { key: 'new_slab', label: 'New slab', for: STRUCT, options: [
    { v: 'sog', l: 'Slab on grade' }, { v: 'mat', l: 'Mat slab' }, { v: 'pt', l: 'Post-tensioned' },
    { v: 'none', l: 'No new slab' }, { v: 'engineer', l: 'Engineer to recommend' },
  ] },
  { key: 'roofing', label: 'Roofing material', for: STRUCT, kind: 'text', placeholder: 'e.g. clay tile, standing seam metal' },
  { key: 'roof_deck', label: 'Roof deck', for: STRUCT, options: YES_NO },
  { key: 'vaulted', label: 'Vaulted ceilings', for: STRUCT, options: YES_NO },
  { key: 'solar', label: 'Solar panels on the roof', for: [...STRUCT, ...ENERGY], options: [
    { v: 'yes', l: 'Yes' }, { v: 'future', l: 'Future' }, { v: 'no', l: 'No' }, { v: 'unknown', l: "Don't know" },
  ] },
  { key: 'lightweight_concrete', label: 'Lightweight concrete on a floor', for: STRUCT, options: YES_NO },
  { key: 'depressed_showers', label: 'Depressed (curbless) showers', for: STRUCT, options: YES_NO },
  { key: 'heavy_tubs', label: 'Heavy bath tubs (stone, cast iron)', for: STRUCT, options: YES_NO },
  { key: 'spa_on_deck', label: 'Spa on a roof deck or elevated deck', for: STRUCT, options: YES_NO },
  { key: 'curbs', label: 'Curbs other than at the garage', for: STRUCT, options: YES_NO },

  // ── energy / Title 24 (2026-10-28) — what a consultant needs to price and model the calcs.
  //    The climate zone, building type, stories and required solar size come from the record
  //    (energyRecordLines below), never asked. Glazing and assemblies are on the drawings. ──
  { key: 'hvac_system', label: 'Heating and cooling', for: ENERGY, options: [
    { v: 'heat_pump', l: 'Heat pump, ducted' }, { v: 'mini_split', l: 'Ductless mini-split' },
    { v: 'furnace_ac', l: 'Gas furnace with air conditioning' }, { v: 'furnace', l: 'Gas furnace, no cooling' },
    { v: 'radiant', l: 'Radiant' }, { v: 'unknown', l: "Don't know" },
  ] },
  { key: 'hvac_existing', label: 'Existing heating and cooling', for: ENERGY, when: (f) => !isNew(f), options: [
    { v: 'replaced', l: 'Replaced' }, { v: 'extended', l: 'Kept and extended to the new area' },
    { v: 'kept', l: 'Kept as is' }, { v: 'unknown', l: "Don't know" },
  ] },
  { key: 'water_heating', label: 'Water heating', for: ENERGY, options: [
    { v: 'heat_pump', l: 'Heat pump water heater' }, { v: 'gas_tankless', l: 'Gas tankless' },
    { v: 'gas_storage', l: 'Gas storage tank' }, { v: 'electric', l: 'Electric resistance' },
    { v: 'unknown', l: "Don't know" },
  ] },
  { key: 'water_existing', label: 'Existing water heater', for: ENERGY, when: (f) => !isNew(f), options: [
    { v: 'replaced', l: 'Replaced' }, { v: 'kept', l: 'Kept' }, { v: 'unknown', l: "Don't know" },
  ] },
  { key: 'battery', label: 'Battery storage', for: ENERGY, options: YES_NO },
  { key: 'pool_heat', label: 'Pool or spa heating', for: ENERGY, options: [
    { v: 'none', l: 'No pool or spa' }, { v: 'heat_pump', l: 'Heat pump' }, { v: 'gas', l: 'Gas heater' },
    { v: 'solar', l: 'Solar' }, { v: 'unheated', l: 'Not heated' }, { v: 'unknown', l: "Don't know" },
  ] },
  { key: 'hvac_designer', label: 'Heating and cooling designed by', for: ['Energy Consultant'], options: [
    { v: 'mep', l: 'An MEP engineer' }, { v: 'design_build', l: 'The contractor (design-build)' },
    { v: 'unknown', l: "Don't know" },
  ] },
]

/** The questions this service is asked, given the answers so far (some depend on the work type). */
export function rfpQuestionsFor(service, facts = {}) {
  return RFP_QUESTIONS.filter((q) => (q.for === 'all' || q.for.includes(service)) && (!q.when || q.when(facts)))
}

/** A question still to answer (choice with no pick). Text questions are never required. */
export function rfpFactsMissing(service, facts = {}) {
  return rfpQuestionsFor(service, facts).filter((q) => q.kind !== 'text' && !facts[q.key])
}

/** The printed answer: the option's label, "To be confirmed" for unknown or blank. */
export function rfpAnswerText(q, value) {
  if (q.kind === 'text') return value ? String(value).trim() : ''
  if (!value || value === 'unknown') return 'To be confirmed'
  const o = (q.options || []).find((x) => x.v === value)
  return o ? o.l : String(value)
}

/**
 * The energy facts the project record already holds (title-sheet data, api/title-sheet.js#assemble):
 * climate zone, building type, stories, the required solar size. Read when the RFP is built and
 * frozen with its scope of work (`scope.record`), never asked and never stored per project.
 */
export function energyRecordLines(d) {
  const p = (d && d.project) || {}
  const c = (d && d.concept) || {}
  const txt = (v) => (v == null ? '' : String(Array.isArray(v) ? v[0] || '' : v).trim())
  const lines = []
  const cz = txt(p.cec_climate_zone)
  lines.push({ label: 'CEC climate zone', answer: cz || 'To be confirmed' })
  if (txt(p.building_type)) lines.push({ label: 'Building type', answer: txt(p.building_type) })
  if (txt(c.number_of_stories)) lines.push({ label: 'Stories', answer: txt(c.number_of_stories) })
  if (txt(c.minimum_required_kwdc) && Number(c.minimum_required_kwdc) > 0) lines.push({ label: 'Minimum solar required', answer: txt(c.minimum_required_kwdc) + ' kWdc' })
  return lines
}

/** [{ label, answer }] for the RFP, in question order; empty text answers left out. A service in
 *  ENERGY gets the record's energy lines first (`scope.record`, frozen with the RFP). */
export function rfpFactLines(service, facts = {}, scope = null) {
  const record = ENERGY.includes(service) && scope && Array.isArray(scope.record) ? scope.record : []
  return record.concat(rfpQuestionsFor(service, facts)
    .map((q) => ({ label: q.label, answer: rfpAnswerText(q, facts[q.key]) }))
    .filter((l) => l.answer))
}

/**
 * A suggested work type from the scope of work (api/_scopeOfWork.js groups): new area with
 * existing area = addition, remodel lines = remodel, new area with nothing existing = new.
 * Only a suggestion — the person confirms it in the facts step.
 */
export function suggestWorkType(scope) {
  const lines = ((scope && scope.groups) || []).filter((g) => g.kind === 'unit').flatMap((g) => g.lines)
  const has = (re) => lines.some((l) => re.test(l))
  const existing = has(/^TOTAL LIVING AREA/)
  const added = has(/^NEW (LIVING|GARAGE|MECH|ELEVATED)/)
  const remodel = has(/^REMODEL /)
  if (added && !existing && !remodel) return 'new'
  if (added && remodel) return 'addition_remodel'
  if (added) return 'addition'
  if (remodel) return 'remodel'
  return null
}
